import { useMutation, useQuery } from "@apollo/client";
import { Button, ButtonGroup, Card } from "@blueprintjs/core";
import { useState } from "react";
import {
  CreateLocationInput,
  Location,
  UpdateLocationInput,
} from "../../api/schema";
import {
  FlexContainer,
  FlexGrow,
  FlexRowStretch,
} from "../../components/flex/Flex";
import {
  DeleteModal,
  DeleteStateHelper,
} from "../../components/form/DeleteModal";
import { ModalInputType } from "../../components/form/Form";
import {
  InputFormMode,
  UpdateStateHelper,
  ValuesVariables,
} from "../../components/form/InputForm";
import { InputModal } from "../../components/form/InputModal";
import { ParentPrettyRender } from "../../components/ParentRenderer";
import { ListView } from "../../components/query/ListView";
import { Spinner } from "../../components/Spinner";
import { UserLanguage } from "../../lang/lang";
import { LocationPrettyRender } from "../../utils/PrettyRenderer";
import {
  CreateLocationMutation,
  CreateLocationResponse,
  DeleteLocationMutation,
  DeleteLocationResponse,
  LocationListQuery,
  LocationListResponse,
  UpdateLocationMutation,
} from "./graphql";

export const LocationListView = (props: any) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalState, setUpdateModalState] = useState<
    UpdateStateHelper<Location>
  >({ open: false, item: undefined });
  const [deleteModalState, setDeleteModalState] = useState<DeleteStateHelper>({
    open: false,
    id: undefined,
    prettyPrint: undefined,
  });

  const [createLocation] = useMutation<
    CreateLocationResponse,
    ValuesVariables<CreateLocationInput>
  >(CreateLocationMutation);

  const [updateLocation] = useMutation<
    Location,
    ValuesVariables<UpdateLocationInput>
  >(UpdateLocationMutation);
  const [deleteLocation] = useMutation<DeleteLocationResponse>(
    DeleteLocationMutation
  );

  const listQuery = useQuery<LocationListResponse>(LocationListQuery, {
    fetchPolicy: "no-cache",
  });

  return (
    <FlexContainer>
      <Card elevation={3}>
        <FlexRowStretch>
          <div style={{ marginRight: 25 }}>
            <h2>{UserLanguage().pages.location.list}</h2>
          </div>
          <FlexGrow />
          <div>
            <Button
              intent="success"
              icon="plus"
              onClick={() => setCreateModalOpen(true)}
            >
              {UserLanguage().actions.create}
            </Button>
          </div>
        </FlexRowStretch>
        <InputModal<Location, CreateLocationResponse, CreateLocationInput>
          keys={{
            name: {
              type: ModalInputType.String,
              icon: "id-number",
              placeholder: UserLanguage().data.name,
            },
            parent: {
              type: ModalInputType.ItemSelect,
              itemKey: "id",
              items: listQuery.data ? listQuery.data.locations : [],
              placeholder: UserLanguage().data.location.parent,
              textRenderer: (i) => LocationPrettyRender(i),
              icon: "map-marker",
            },
          }}
          open={createModalOpen}
          texts={UserLanguage().pages.location.actions}
          onClose={() => setCreateModalOpen(false)}
          mutation={createLocation}
          reformId={["parent"]}
          onSuccess={() => listQuery.refetch()}
          resetOnClose
          mode={InputFormMode.Create}
        />
        <InputModal<Location, Location, UpdateLocationInput>
          keys={{
            id: {
              type: ModalInputType.Display,
              label: UserLanguage().data.id,
            },
            name: {
              type: ModalInputType.String,
              icon: "id-number",
              label: UserLanguage().data.name,
            },
            parent: {
              type: ModalInputType.ItemSelect,
              items: listQuery.data?.locations,
              itemKey: "name",
              label: UserLanguage().data.location.parent,
              placeholder: UserLanguage().data.location.parent,
              icon: "map-marker",
            },
          }}
          open={updateModalState.open}
          input={updateModalState.item}
          texts={UserLanguage().pages.location.actions}
          onClose={() => setUpdateModalState({ open: false, item: undefined })}
          mutation={updateLocation}
          reformId={["parent"]}
          onSuccess={() => {
            listQuery.refetch();
            setUpdateModalState({ open: false, item: undefined });
          }}
          resetOnClose
          mode={InputFormMode.Edit}
        />
        <DeleteModal<DeleteLocationResponse>
          id={deleteModalState.id}
          open={deleteModalState.open}
          mutation={(id) => deleteLocation({ variables: { id } })}
          texts={UserLanguage().pages.location.actions}
          resetOnClose
          onClose={() => {
            setDeleteModalState({ open: false, id: undefined });
          }}
          onSuccess={() => {
            setDeleteModalState({ open: false, id: undefined });
            listQuery.refetch();
          }}
          type={UserLanguage().data.location.label}
          prettyPrint={deleteModalState.prettyPrint}
        />
        <ListView<LocationListResponse, Location>
          cols={{
            id: { label: UserLanguage().data.id },
            name: { label: UserLanguage().data.name },
            parent: {
              label: UserLanguage().data.location.parent,
              render: (i) => ParentPrettyRender(i.parent),
            },
            actions: {
              label: UserLanguage().actions.label,
              render: (item) => (
                <ButtonGroup>
                  <Button
                    intent="primary"
                    rightIcon="edit"
                    onClick={() =>
                      setUpdateModalState({
                        open: true,
                        item,
                      })
                    }
                  >
                    {UserLanguage().actions.edit}
                  </Button>
                  <Button
                    intent="danger"
                    rightIcon="delete"
                    onClick={() =>
                      setDeleteModalState({
                        open: true,
                        id: item.id,
                        prettyPrint: `${UserLanguage().data.name}: ${
                          item.name
                        }`,
                      })
                    }
                  >
                    {UserLanguage().actions.delete}
                  </Button>
                </ButtonGroup>
              ),
            },
          }}
          error={<p>Error</p>}
          loading={<Spinner />}
          itemKey="locations"
          query={listQuery}
        />
      </Card>
    </FlexContainer>
  );
};
